import React, { useState } from "react";
import { T } from "services/lang/LanguageService";

import { useGetPaginatedDatasets } from "api/api-hooks/datasets-hooks";
import { DatasetInfo } from "api/model/dataset/DatasetInfo";
import PaginatorWithoutTotalCount from "components/basic/PaginatorWithoutTotalCount";
import { getSearchParams, mergeUniqueObjects } from "components/dashboard/dataset-window/utils/utils";
import useDebounce from "hooks/useDebounce";
import { useHasPermissions } from "hooks/useHasPermissions";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Divider } from "primereact/divider";
import { Tag } from "primereact/tag";
import { Tooltip } from "react-tooltip";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import useUrlQueryState from "hooks/useUrlQueryState";
import { Checkbox } from "primereact/checkbox";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import { Dataset } from "./models/types";
import SearchDatasetsInput from "./SearchDatasetsInput";

type DatasetSelectionProps = {
    datasets: Dataset[];
    setDatasets: (arr: Dataset[]) => void;
    useSearchParameters?: boolean;
};

const DatasetSelection = ({ datasets, setDatasets, useSearchParameters }: DatasetSelectionProps) => {
    const [searchBar, setSearchBar] = !useSearchParameters ? useState("") : useUrlQueryState("datasetsQuery", "");
    const [lazyParams, setLazyParams] = useState({ page: 0, rows: 30 });
    const [proxied, setProxied] = useState(false);

    const isSuperUser = useHasPermissions("SUPER_USER");
    const companyCtx = useCurrentCompanyContext();

    const searchValue = useDebounce<string>(searchBar, {
        delay: 400,
        callbackOnTime: () => {
            setLazyParams((old) => ({ ...old, page: 0 }));
        },
    });

    const {
        data,
        isLoading,
        isError,
    } = useGetPaginatedDatasets(
        { ...getSearchParams(searchValue, lazyParams), companyRef: companyCtx?.companyReference, proxied },
        { refetchOnWindowFocus: false }
    );

    const handleDatasetSelection = ({ originalEvent, value }: { value: any; originalEvent: any }) => {
        if (originalEvent && originalEvent.altKey) {
            setDatasets(value);
            return;
        }
        if (originalEvent && originalEvent.shiftKey) {
            const uniqueSelection = mergeUniqueObjects(datasets, value);
            setDatasets([...uniqueSelection]);
        } else if (datasets.some((dataset) => dataset.identifier === value[0].identifier)) {
            setDatasets([...datasets.filter((obj) => obj.identifier !== value[0].identifier)]);
        } else {
            setDatasets([...datasets, ...value]);
        }
    };

    const rowClass = (rowData: DatasetInfo) => ({
        "row-selected": datasets.find((s) => s.identifier === rowData.identifier),
    });

    if (isError) {
        return <div>{T("error.error-while-fetching-datasets")}...</div>;
    }
    const processedData = data?.map((pd) => new DatasetInfo(pd));

    const titleBodyTemplate = (rowData: DatasetInfo) => {
        const datasetClassAbbreviated = rowData.datasetClass?.split("_").map((s: string) => s[0]).join("");
        const titleWrap = (title: string | null) => {
            if (rowData.deleted) {
                return <del className="font-italic">{title}</del>;
            }
            return <span>{title}</span>;
        };
        return (
            <span className="flex align-items-center">
                {isSuperUser && (
                    <>
                        <Tooltip id="dataset-class" opacity={0.5} offset={0.5} />
                        <Tag
                            data-tooltip-id="dataset-class"
                            className="text-black-alpha-50 bg-transparent"
                            value={datasetClassAbbreviated}
                            data-tooltip-content={rowData.datasetClass}
                        />
                    </>
                )}
                {titleWrap(rowData.title)}
            </span>
        );
    };

    return (
      <div className="selectionPanel fullWidth">
        <SearchDatasetsInput currentValue={searchBar} onChange={setSearchBar} />
        <PermissionGuard permissions="SUPER_USER">
          <div className="flex align-items-center my-1 ml-2 proxy-checkbox">
            <Checkbox
              inputId="proxied"
              name="proxied"
              onChange={() => setProxied(!proxied)}
              checked={proxied}
              />
            <label htmlFor="proxied" className="ml-2 text-sm">
              {T("label.search-for-proxied-datasets")}
            </label>
          </div>
        </PermissionGuard>
        <Divider className="my-2"/>
        <div style={{ flexGrow: 1 }}>
          <DataTable
            value={processedData?.map((pd: DatasetInfo) => ({
              ...pd,
              formatedTitle: pd.toString(),
            }))}
            lazy
            selectionMode="multiple"
            onSelectionChange={handleDatasetSelection}
            dataKey="identifier"
            responsiveLayout="scroll"
            className="text row fullWidth"
            rowClassName={rowClass}
            emptyMessage={T("message.no-datasets-found")}
            showGridlines
            loading={isLoading}
          >
            <Column
              key="dataset"
              field="title"
              header={T("word.dataset")}
              className="row"
              style={{ width: "30rem" }}
              body={titleBodyTemplate}
            />
            <Column
              key="unit"
              field="unit"
              header={T("word.unit")}
              className="row"
              style={{ width: "10px", whiteSpace: "nowrap" }}
            />
          </DataTable>
        </div>
        <PaginatorWithoutTotalCount
          page={lazyParams.page}
          maxRows={lazyParams.rows}
          rows={data?.length}
          setPage={page => setLazyParams(old => ({ ...old, page }))}
        />
      </div>
    );
};

export default DatasetSelection;
